import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Hub } from 'aws-amplify';
import { useAppDispatch } from '../app/hooks';
import { fetchUserDataByToken } from '../app/store/userSlice';
import { MAX_SESSION_LENGTH, refreshAuthSession } from '../services/auth';
import useLogout from './useLogout';

// Public entry points that don't require a prior session - visiting these while logged out shouldn't force a redirect.
export const PUBLIC_ROUTES = ['/', '/sign-up', '/admin-sso-login'];

// True immediately after an OAuth/SAML redirect back from Cognito, before Amplify finishes the token
// exchange and rewrites the URL - session data won't exist in localStorage yet during that window.
export const isOAuthCallbackInProgress = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
};

// App-wide session handling: keeps `user` populated after any sign-in (regardless of which page the
// OAuth/SAML redirect happens to land on) and signs out a user whose session has expired or is missing.
export default function useGlobalAuthSession() {
  const dispatch = useAppDispatch();
  const handleLogout = useLogout();
  const location = useLocation();

  useEffect(() => {
    const listener = async (data: any) => {
      const event = data.payload.event;
      if (event === 'signIn' || event === 'cognitoHostedUI') {
        await refreshAuthSession();
        localStorage.setItem('fixedLoginTime', Date.now().toString());
        dispatch(fetchUserDataByToken());
      }
    };

    Hub.listen('auth', listener);

    return () => {
      Hub.remove('auth', listener);
    };
  }, [dispatch]);

  /**
    * Checks token expiry using `amplifyTokenExpiry`
    * Enforces 24h max session using `fixedLoginTime`
    * Logs out if either check fails or if data is missing
  */
  useEffect(() => {
    const validateSession = () => {
      if (PUBLIC_ROUTES.includes(location.pathname) || isOAuthCallbackInProgress()) return;

      try {
        const tokenExpiryStr = localStorage.getItem('amplifyTokenExpiry');
        const loginTimeStr = localStorage.getItem('fixedLoginTime');

        if (!tokenExpiryStr || !loginTimeStr) {
          throw new Error('Missing session data in localStorage');
        }

        const tokenExpiry = parseInt(tokenExpiryStr, 10);
        const loginTime = parseInt(loginTimeStr, 10);

        if (isNaN(tokenExpiry) || isNaN(loginTime)) {
          throw new Error('Invalid token or login time format');
        }

        const now = Date.now();
        const sessionExpiresAt = loginTime + MAX_SESSION_LENGTH;

        if (now > sessionExpiresAt) {
          throw new Error('Session expired due to MAX_SESSION_LENGTH');
        }

        if (now > tokenExpiry) {
          throw new Error('Token has expired');
        }
      } catch (err) {
        console.error('Session validation failed:', err);
        handleLogout();
      }
    };

    validateSession();
  }, [dispatch]);
}
